import React, { useEffect, useRef, useState } from "react"
import "./catering-order.scss"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.min.css"

import { formatDateToYYYYMMDD, generateTimeSlots } from "config/helper"
import axios from "config/axios"
import { useApiDefaults } from "hooks/useApiDefaults"

const timeSlots = generateTimeSlots("12:00 AM", "11:30 PM", 30)

const DeliveryTypes = {
  DELIVERY: "Delivery",
  PICKUP: "Pick Up",
}

const menuItems = [
  "Appetizers",
  "Trays & Platters",
  "Fresh Salads",
  "Deli Sandwiches",
  "Entrees",
  "Pasta",
  "Desserts & Coffee",
  "Sides",
  "Add-On's",
]

const eventTypes = [
  "Private Party",
  "Business Meeting",
  "Wedding",
  "Holiday",
  "Other",
]

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  date: new Date(),
  time: timeSlots[0],
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  eventType: "",
  peopleCount: "",
  orderMethod: DeliveryTypes.DELIVERY,
  menuTypes: [],
  specialRequests: "",
  submitMessage: "",
}

function CateringOrder() {
  const firstNameRef = useRef(null)
  const [saving, setSaving] = useState(false)

  const { RSAClientId, ClientStoreId } = useApiDefaults()

  const [formData, setFormData] = useState(initialState)

  const [errors, setErrors] = useState({})

  function validateForm() {
    const newErrors = {}

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required"

    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required"

    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleInputChange(e) {
    const { name, value, type, checked } = e.target

    if (type === "checkbox") {
      const menuTypes = checked
        ? [...formData.menuTypes, value]
        : formData.menuTypes.filter(item => item !== value)
      setFormData(prev => ({ ...prev, menuTypes }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  async function handleSubmit(e) {
    try {
      e.preventDefault()
      setSaving(true)

      if (saving) return

      if (validateForm()) {
        const response = await axios.post("/InquireOrder", {
          RSAClientId,
          ClientStoreId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email_id: formData.email,
          phone_number: formData.phone,
          order_date: formatDateToYYYYMMDD(formData.date),
          order_time: formData.time,
          shipping_method: formData.orderMethod,
          address1: formData.addressLine1,
          address2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zipcode: formData.zipCode,
          type_of_event: formData.eventType,
          howmany_people: formData.peopleCount,
          message: formData.specialRequests,
          interested_in: formData.menuTypes,
        })

        if (response?.data?.status === "success") {
          setFormData(() => ({ ...initialState, submitMessage: response?.data?.message ?? "Email sent successfully!" }))
        } else {
          setFormData(prev => ({ ...prev, submitMessage: response?.data?.message ?? "Something went wrong!" }))
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error)
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (firstNameRef.current) {
      firstNameRef.current.focus()
    }
  }, [])

  return (
    <div className="catering-order">
      <div className="container">
        <h2>Catering Inquiry Form</h2>
        <p>FILL OUT FORM BELOW TO INQUIRE ABOUT A CATERING ORDER.</p>
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <h3>Name</h3>
                <div className="form-group form-field-row">
                  <div
                    className="label-main-wrap"
                    style={{ marginBottom: "5px" }}
                  >
                    <label className="sublabel row-block">
                      <div>
                        First<span>*</span>
                      </div>
                      <input
                        type="text"
                        id="first-name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        style={{ margin: "0" }}
                        autoComplete="given-name"
                        ref={firstNameRef}
                      />
                    </label>
                    {errors.firstName && (
                      <div className="error">{errors.firstName}</div>
                    )}
                  </div>
                  <div
                    className="label-main-wrap"
                    style={{ marginBottom: "5px" }}
                  >
                    <label className="sublabel row-block">
                      <div>
                        Last<span>*</span>
                      </div>
                      <input
                        type="text"
                        id="last-name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        style={{ margin: "0" }}
                        autoComplete="family-name"
                      />
                    </label>
                    {errors.lastName && (
                      <div className="error">{errors.lastName}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <h3>Contact</h3>
                <div className="form-data">
                  <div
                    className="label-main-wrap"
                    style={{ marginBottom: "5px" }}
                  >
                    <label className="sublabel row-block required">
                      <div>
                        Email<span>*</span>
                      </div>
                      <input
                        type="text"
                        id="email"
                        name="email"
                        value={formData.email}
                        style={{ margin: "0" }}
                        onChange={handleInputChange}
                        autoComplete="email"
                      />
                    </label>
                    {errors.email && (
                      <div className="error">{errors.email}</div>
                    )}
                  </div>
                  <label className="sublabel row-block">
                    Phone
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      autoComplete="tel"
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <h3 className="label-title">Event Details</h3>
                <div className="form-group form-field-row">
                  <label className="sublabel">
                    <DatePicker
                      selected={formData.date}
                      onChange={date => setFormData(prev => ({ ...prev, date }))}
                      shouldCloseOnSelect={true}
                      dateFormat="yyyy-MM-dd"
                    />
                    Date
                  </label>
                  <label className="sublabel">
                    <select
                      name="time"
                      id="time"
                      value={formData.time}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Time</option>
                      {timeSlots.map((time, index) => (
                        <option key={index} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>{" "}
                    Time
                    {errors.time && <div className="error">{errors.time}</div>}
                  </label>
                </div>
                <label className="field-label">
                  Location
                  <input
                    type="text"
                    id="address-line-1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    placeholder="Address Line 1"
                    autoComplete="address-line1"
                  />
                  {errors.addressLine1 && (
                    <div className="error">{errors.addressLine1}</div>
                  )}
                  <div className="sublabel"> Address Line 1</div>
                  <input
                    type="text"
                    id="address-line-2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    placeholder="Address Line 2"
                    autoComplete="address-line2"
                  />
                  <div className="sublabel">Address Line 2</div>
                </label>
                <div className="form-group form-field-row">
                  <label className="sublabel" htmlFor="event-city">
                    <input
                      type="text"
                      id="event-city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                    City
                    {errors.city && <div className="error">{errors.city}</div>}
                  </label>
                  <label className="sublabel" htmlFor="event-city">
                    <input
                      type="text"
                      id="event-state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                    State
                    {errors.state && (
                      <div className="error">{errors.state}</div>
                    )}
                  </label>
                </div>
                <label className="sublabel" htmlFor="event-zip">
                  <input
                    type="number"
                    id="event-zip"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                  />
                  Zip Code
                  {errors.zipCode && (
                    <div className="error">{errors.zipCode}</div>
                  )}
                </label>
                <label
                  className="field-label"
                  htmlFor="event-type"
                  style={{ marginTop: "10px" }}
                >
                  Type of Event
                  <select
                    id="event-type"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Event Type</option>
                    {eventTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.eventType && (
                    <div className="error">{errors.eventType}</div>
                  )}
                </label>
                <label className="field-label">
                  How Many People
                  <input
                    type="number"
                    name="peopleCount"
                    value={formData.peopleCount}
                    onChange={handleInputChange}
                    placeholder="Fill in estimate"
                  />
                  {errors.peopleCount && (
                    <div className="error">{errors.peopleCount}</div>
                  )}
                </label>
              </div>
              <div className="form-group">
                <h3 className="label-title">Order Details</h3>
                <ul id="order-method">
                  <li className="choice-1 depth-1">
                    <input
                      type="radio"
                      id="delivery"
                      name="orderMethod"
                      value={DeliveryTypes.DELIVERY}
                      checked={formData.orderMethod === DeliveryTypes.DELIVERY}
                      onChange={handleInputChange}
                    />
                    <label className="sublabel" htmlFor="delivery">
                      {DeliveryTypes.DELIVERY}
                    </label>
                  </li>
                  <span>OR</span>
                  <li className="choice-2 depth-1">
                    <input
                      type="radio"
                      id="pickup"
                      name="orderMethod"
                      value={DeliveryTypes.PICKUP}
                      checked={formData.orderMethod === DeliveryTypes.PICKUP}
                      onChange={handleInputChange}
                    />
                    <label className="sublabel" htmlFor="pickup">
                      {DeliveryTypes.PICKUP}
                    </label>
                  </li>
                </ul>
                {errors.orderMethod && (
                  <div className="error">{errors.orderMethod}</div>
                )}
                <div className="checkbox-group" style={{ marginTop: "20px" }}>
                  <div className="catering-sub-title">Interested In:</div>
                  <div className="checkbox-group">
                    {menuItems.map(item => (
                      <div className="choice" key={item}>
                        <input
                          type="checkbox"
                          name="menuTypes"
                          value={item}
                          id={item}
                          checked={formData.menuTypes.includes(item)}
                          onChange={handleInputChange}
                        />
                        <label htmlFor={item}>{item}</label>
                      </div>
                    ))}
                  </div>
                  {errors.menuTypes && (
                    <div className="error">{errors.menuTypes}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="fill-detail-section">
              <div className="fill-detail-main">
                <div className="fill-detail-title">
                  <strong>Fill in Details:</strong>
                  <span>
                    Have any food allergies or <br />
                    special requests? Let us know <br />
                    and we will do our best to <br />
                    accommodate.
                  </span>
                </div>
                <textarea
                  rows="5"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  placeholder="Write your message here..."
                />
              </div>
              <div className="btn-wrap">
                <span className="submit-text">
                  Submit your order inquiry and let us figure out the details!
                </span>
                <button type="submit" disabled={saving}>
                  {saving ? "Submitting..." : "Submit Order"}
                </button>
                {formData.submitMessage ? <div>
                  <span className="error" style={{ marginTop: "5px" }}>{formData.submitMessage}</span>
                </div> : null}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CateringOrder
